import {AyTracking} from '../../ay-tracking';
import {
  RESET_FILTER_SELECTION,
  TrackingElementContext,
  TrackingSectionContext,
} from '../../types';
import {
  TrackingProductAdditionalData,
  additionalDataForAddToBasket,
  additionalDataForAddToWishlist,
} from './additionalData';
import {TrackingClientStateHolder} from './helpers/clientState';
import {replayLastContentViewBefore} from './trackingHandlers/replayLastContentViewBefore';
import {trackABTestGroupAssignment} from './trackingHandlers/trackABTestGroupAssignment';
import {trackAccountRegistration} from './trackingHandlers/trackAccountRegistration';
import {trackAdpSizeChange} from './trackingHandlers/trackAdpSizeChange';
import {trackAdpView} from './trackingHandlers/trackAdpView';
import {trackBasketAction} from './trackingHandlers/trackBasketAction';
import {trackCategoryView} from './trackingHandlers/trackCategoryView';
import {trackContentInteraction} from './trackingHandlers/trackContentInteraction';
import {trackContentView} from './trackingHandlers/trackContentView';
import {trackCookieBanner} from './trackingHandlers/trackCookieBanner';
import {trackCustomerData} from './trackingHandlers/trackCustomerData';
import {trackExperimentActivation} from './trackingHandlers/trackExperimentActivation';
import {trackExpressCheckout} from './trackingHandlers/trackExpressCheckout';
import {trackFilterSelection} from './trackingHandlers/trackFilterSelection';
import {trackFitAnalytics} from './trackingHandlers/trackFitAnalytics';
import {trackHotDrops} from './trackingHandlers/trackHotDrops';
import {trackHouston} from './trackingHandlers/trackHouston';
import {trackLiveShoppingPlayer} from './trackingHandlers/trackLiveShoppingPlayer';
import {trackLogout} from './trackingHandlers/trackLogout';
import {trackMediaPollResult} from './trackingHandlers/trackMediaPollResult';
import {trackMembership} from './trackingHandlers/trackMembership';
import {trackNewsletter} from './trackingHandlers/trackNewsletter';
import {trackNotification} from './trackingHandlers/trackNotification';
import {trackOrderSuccessPage} from './trackingHandlers/trackOrderSuccessPage';
import {trackOriginClick} from './trackingHandlers/trackOriginClick';
import {trackOspError} from './trackingHandlers/trackOspError';
import {trackOutfitView} from './trackingHandlers/trackOutfitView';
import {trackProductInteraction} from './trackingHandlers/trackProductInteraction';
import {trackProductListSort} from './trackingHandlers/trackProductListSort';
import {trackProductListTileView} from './trackingHandlers/trackProductListTileView';
import {trackProductReview} from './trackingHandlers/trackProductReview';
import {trackPromotionInteraction} from './trackingHandlers/trackPromotionInteraction';
import {trackSearch} from './trackingHandlers/trackSearch';
import {trackSearchSuggest} from './trackingHandlers/trackSearchSuggest';
import {trackShopFeature} from './trackingHandlers/trackShopFeature';
import {trackStoryView} from './trackingHandlers/trackStoryView';
import {trackWishlistAction} from './trackingHandlers/trackWishlistAction';

export const createGtmTrackingClient = ({
  clientState,
  enableSynchronousEvents,
}: {
  clientState: TrackingClientStateHolder;
  enableSynchronousEvents?: boolean;
}): AyTracking & {
  additionalDataForAddToBasket: ({
    productId,
    variantId,
    productSponsoredType,
  }: {
    productId: number;
    variantId: number;
    productSponsoredType?: string;
  }) => TrackingProductAdditionalData;
  additionalDataForAddToWishlist: ({
    section,
    element,
  }: {
    section: TrackingSectionContext;
    element: TrackingElementContext;
  }) => TrackingProductAdditionalData;
} => {
  if (enableSynchronousEvents) {
    window.enableSynchronousEvents = true;
  }
  return {
    additionalDataForAddToBasket: additionalDataForAddToBasket(clientState),
    additionalDataForAddToWishlist: additionalDataForAddToWishlist(clientState),
    trackContentView,
    trackCategoryView,
    trackAdpView,
    trackAdpSizeChange,
    trackOutfitView,
    trackStoryView,
    trackCustomerData,
    trackProductInteraction,
    trackBasketAction,
    trackWishlistAction,
    trackPromotionInteraction,
    trackOrderSuccessPage,
    trackOspError,
    trackHouston,
    trackMainFilterSelection: ({selection, action, label}) =>
      trackFilterSelection({
        selection:
          selection === RESET_FILTER_SELECTION
            ? selection
            : {...selection, type: 'main'},
        action,
        label,
      }),
    trackQuickFilterSelection: ({selection, action, label}) =>
      trackFilterSelection({
        selection: {...selection, type: 'quick'},
        action,
        label,
      }),
    trackProductListSort,
    trackProductListTileView,
    trackContentInteraction,
    trackFitAnalytics,
    trackCookieBanner,
    trackNewsletter,
    trackABTestGroupAssignment,
    trackExperimentActivation,
    trackShopFeature,
    trackSearch,
    trackExpressCheckout,
    trackNotification,
    trackSearchSuggest,
    trackMediaPollResult,
    trackMembership,
    trackAccountRegistration,
    trackHotDrops,
    trackProductReview,
    trackOriginClick,
    trackLogout,
    trackLiveShoppingPlayer,
    replayLastContentViewBefore,
  };
};
